import type { HeadersFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { redirect } from '@remix-run/node';
import { type ClientLoaderFunctionArgs, useOutletContext, useRouteLoaderData } from '@remix-run/react';

import CommentsSection from '~/pages/post/CommentsSection';
import GatedContent from '~/pages/post/GatedContent';
import OnScrollActions from '~/pages/post/OnScrollActions';
import PostHTML from '~/pages/post/PostHTML';
import RecommendedPosts from '~/pages/post/RecommendedPosts';

import { ComponentErrorBoundary, DefaultErrorBoundaryFallback } from '~/components/ComponentErrorBoundary';
import PostPaddingContainer from '~/components/Layout/PostPaddingContainer';
import BlueskyEmbed from '~/components/Scripts/BlueskyEmbed';
import StocktwitsEmbed from '~/components/Scripts/StocktwitsEmbed';
import TwitterEmbed from '~/components/Scripts/TwitterEmbed';
import StaticPage from '~/components/StaticPage';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { Paywall } from '~/interfaces/paywall';
import type { Post as IPost } from '~/interfaces/post';
import type { Publication, WebThemeV2 } from '~/interfaces/publication';
import type { Site } from '~/interfaces/site';

import useCreatePageView from '~/hooks/useLoaders/useCreatePageView';
import useRecommendedPosts from '~/hooks/useLoaders/useRecommendedPosts';
import useSetLastViewedResource from '~/hooks/useSetLastViewedResource';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import getPaywall from '~/services/api/paywalls/getPaywall.server';
import getDraftPost from '~/services/api/posts/getDraftPost.server';
import getPost from '~/services/api/posts/getPost.server';
import getPublication from '~/services/api/publication/getPublication.server';
import getSite from '~/services/api/sites/getSite.server';
import { getRenderableFonts } from '~/services/static_page';

import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getMetaDataLinks from '~/utils/getMetaDataLinks';
import getSameAsArray from '~/utils/getSameAsArray';
import setCacheHeaders from '~/utils/setCacheHeaders/index.server';

import useClickTracking from './_hooks/useClickTracking';
import { addTtsAudio } from './_utils/addTtsAudio';
import { getTruncatedHml } from './_utils/getTruncatedHtml';
import { processHtml } from './_utils/processHtml';
import { rewriteImages } from './_utils/rewriteImages';
import { stripHtml } from './_utils/stripHtml';
import { stripNoFollow } from './_utils/stripNoFollow';

import { PostPageContext } from '~/context/PostPageContext';
import { PostPageProvider } from '~/context/PostPageProvider';
import type { Page } from '~/interfaces/page';
import type { PostMergeTags } from '~/interfaces/post_merge_tags';
import type { Subscription } from '~/interfaces/subscriptions';
import getPage from '~/services/api/pages/getPage.server';
import getPreviewSubscription from '~/services/api/preview_subscription/getPreviewSubscription.server';
import { loaderSession } from '~/session';
import { shouldRevalidateLoaderData } from '~/utils/shouldRevalidateLoaderData';

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const post = data?.post;
  const publication = data?.publication;
  const requestUrl = data?.requestUrl;

  if (!post || !publication) {
    return [
      { title: '404 - Not Found' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !post,
    overrides: {
      title: `${post?.meta_default_title}`,
      description: post?.meta_default_description,
      'og:url': `${publication?.url}p/${post?.slug}`,
      'og:title': `${post?.meta_og_title}`,
      'og:description': post?.meta_og_description,
      'og:site_name': `${publication?.name}`,
      'og:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url,
      'og:image:alt': `${post?.web_subtitle || publication?.description}`,
      'twitter:url': `${publication?.url}p/${post?.slug}`,
      'twitter:title': `${post?.meta_twitter_title}`,
      'twitter:description': post?.meta_twitter_description,
      'twitter:image': post?.image_url || publication?.thumbnail?.url || publication?.logo?.url,
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      identifier: post?.slug,
      url: requestUrl,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': requestUrl,
      },
      headline: post?.web_title,
      description: post?.web_subtitle,
      datePublished: post?.override_scheduled_at,
      dateModified: post?.updated_at,
      isAccessibleForFree: !post?.hide_premium_post,
      image: {
        '@type': 'ImageObject',
        url: post?.image_url,
      },
      author: {
        '@type': 'Person',
        name: post?.authors?.[0]?.name,
        image: {
          '@type': 'ImageObject',
          contentUrl: post?.authors?.[0]?.profile_picture?.url,
          thumbnailUrl: post?.authors?.[0]?.profile_picture?.thumb?.url,
        },
      },
      publisher: {
        '@type': 'Organization',
        name: publication?.name,
        url: publication?.url,
        identifier: publication?.id,
        sameAs: publication ? getSameAsArray(publication) : undefined,
        logo: {
          '@type': 'ImageObject',
          url: publication?.logo?.url,
          contentUrl: publication?.logo?.url,
          thumbnailUrl: publication?.logo?.thumb?.url,
        },
        image: {
          '@type': 'ImageObject',
          url: publication?.logo?.url,
          contentUrl: publication?.logo?.url,
          thumbnailUrl: publication?.logo?.thumb.url,
        },
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url,
              name: publication?.name,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': requestUrl,
              name: post?.web_title,
            },
          },
        ],
      },
    },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control': loaderHeaders.get('cache-control') || 'max-age=0', // no-cache
    'Cache-Tag': 'orchid-page',
  };
};

export interface PostLoaderData {
  hasPostPageV2: boolean;
  post: IPost;
  postMergeTags?: PostMergeTags;
  page?: Page;
  isPostPremium: boolean;
  site: Site;
  webTheme: WebThemeV2;
  publication: Publication;
  commentsEnabled: boolean;
  recommendedReadingEnabled: boolean;
  requestUrl: string;
  html: string;
  truncatedHtml: string;
  onlyRenderComments: boolean;
  isDraft: boolean;
  paywall: Paywall | null;
  currentSubscriberId: string;
  currentTierId: string;
  renderableFonts: string[];
  previewSubscription?: Subscription;
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const showComments = url.searchParams.get('comments') === 'true';
  const isDraft = url.searchParams.get('draft') === 'true';
  const giftContent = url.searchParams.get('gift_content');
  const currentSubscriberId = url.searchParams.get('current_subscriber_id') || '';
  const currentTierId = url.searchParams.get('current_tier_id') || '';

  if (!params.slug) {
    return redirect('/');
  }

  const promises: [Promise<Publication>, Promise<Site>, Promise<IPost>] = [
    getPublication(request, session),
    getSite(request, session),
    isDraft
      ? getDraftPost({
          request,
          session,
          slug: params.slug || '',
          currentSubscriberId,
          currentTierId,
        })
      : getPost(request, session, params.slug || '', giftContent ? giftContent : undefined),
  ];
  const [publication, site, post] = await Promise.all(promises);

  const isPremiumPost = post.audience === 'premium' || post.audience === 'both';
  let paywall = null;
  if (isPremiumPost && !post.force_premium) {
    paywall = await getPaywall(request, session, post.paywall_id);
  }

  if (post.redirect) return redirect('/'); // Redirects to index feed when no post is found

  // Post page v2
  if (site?.viewable_site_version?.settings?.post_page_v2_enabled) {
    try {
      // We wrap this within try/catch block so we can continue
      // rendering post page v1 if the page with slug `p/$slug` is not found,
      const page = await getPage(request, session, {
        pathname: 'p/$slug',
        postSlug: params.slug,
        draft: url.searchParams.get('draft') || undefined,
      });
      const renderableFonts = await getRenderableFonts(site, (page as Page) || undefined, post.post_theme);

      // When previewing a post via Swarm, a jwt token is sent with payload tier_id or subscriber_id
      // Make a request to get a preview subscription based on the payload of the jwt token
      // The subscription in the response is used in `PostContent` component to
      // render the content based on visibility settings
      const previewJwtToken = url.searchParams.get('preview_jwt_token');
      let previewSubscription: Subscription | undefined;
      if (previewJwtToken && isDraft) {
        previewSubscription = await getPreviewSubscription(request, session, previewJwtToken);
      }

      return Response.json(
        {
          previewSubscription,
          hasPostPageV2: true,
          post,
          postMergeTags: {},
          site,
          page,
          publication,
          commentsEnabled: post.comments_enabled,
          recommendedReadingEnabled: publication.web_template.enable_recommended_reading,
          requestUrl: url.href,
          onlyRenderComments: showComments,
          isDraft,
          paywall,
          currentSubscriberId,
          currentTierId,
          renderableFonts,
        },
        {
          // Note: Keep MaxAge 0 to avoid caching in the browser, we only want to cache via the CDN with sMaxAge.
          headers: setCacheHeaders({
            maxAge: 0,
            sMaxAge: 60 * 60 * 24 * 30, // 30 days
            staleWhileRevalidate: 60,
          }),
        },
      );
    } catch {
      // Do nothing, render post page v1
    }
  }

  // strip the <html><head><body> tags from the raw post html
  // replaces them with <div> tags
  let html = stripHtml(post.html);

  // post process the HTML by running it through a series of processor functions
  // 1. rewrite CDN image URLs to use the correct domain
  // 2. remove nofollow from links
  // 3. add text-to-speech audio to the post
  html = processHtml(html, [
    rewriteImages,
    publication.noindex_enabled === false ? stripNoFollow : null,
    post.text_to_speech_config?.audio_url ? addTtsAudio(post.text_to_speech_config) : null,
  ]);

  const renderableFonts = await getRenderableFonts(site);

  return Response.json(
    {
      hasPostPageV2: false,
      post,
      isPostPremium: post?.audience === 'premium',
      site,
      publication,
      commentsEnabled: post.comments_enabled,
      recommendedReadingEnabled: publication.web_template.enable_recommended_reading,
      requestUrl: url.href,
      html,
      truncatedHtml: getTruncatedHml(html),
      onlyRenderComments: showComments,
      isDraft,
      paywall,
      currentSubscriberId,
      currentTierId,
      renderableFonts,
    },
    {
      // Note: Keep MaxAge 0 to avoid caching in the browser, we only want to cache via the CDN with sMaxAge.
      headers: setCacheHeaders({
        maxAge: 0,
        sMaxAge: 10,
        staleWhileRevalidate: 10,
      }),
    },
  );
};

export const clientLoader = async ({ serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<PostLoaderData>();

  // Short circuit if the post page v2 is not enabled
  if (!serverData?.hasPostPageV2) {
    return serverData;
  }

  // If the post page v2 is enabled, fetch the merge tags for the post
  const mergeTagsResponse = await fetch(`/merge_tags/${serverData?.post?.id}?draft=${serverData?.isDraft}`, {
    method: 'GET',
    headers: { 'Sec-Fetch-Dest': 'empty' },
  });
  const mergeTags = await mergeTagsResponse.json();
  const postMergeTags = ('built_substitutions' in mergeTags ? mergeTags.built_substitutions : {}) as PostMergeTags;

  return {
    ...serverData,
    postMergeTags,
  };
};

clientLoader.hydrate = true;

export default function Post() {
  const {
    post,
    site,
    page,
    html,
    truncatedHtml,
    publication,
    recommendedReadingEnabled,
    commentsEnabled,
    onlyRenderComments,
    isDraft,
    paywall,
    currentTierId,
    currentSubscriberId,
    hasPostPageV2,
  } = useTypedLoaderData<PostLoaderData>();
  const { subscription } = useRouteLoaderData<typeof loader>('root');
  const { cookies, setCookie, cookiesSet, isLoggedIn, admin } = useOutletContext<OutletContext>();
  const viewingAsNewVisitor = currentTierId === 'none';

  // Create Page View
  const visitToken = cookies.visit_token ?? '';
  const pageViews = cookies.page_view ?? '';
  useCreatePageView({
    visitToken: visitToken,
    pageViews: pageViews || '',
    post,
    setCookie,
    cookiesSet,
  });

  useSetLastViewedResource({
    guid: `Post:${post.id}`,
    setCookie,
    cookiesSet,
  });

  // setup click tracking for links
  useClickTracking({ post, visitToken });

  if (hasPostPageV2) {
    return (
      <PostPageProvider>
        <PostPageContext.Consumer>
          {(value) => {
            return (
              <>
                {!value?.onlyRenderComments && <StocktwitsEmbed />}
                <StaticPage site={site} page={page}>
                  {!value?.onlyRenderComments && (
                    <>
                      <TwitterEmbed updateOn={value?.isPopupDisabled} />
                      <BlueskyEmbed updateOn />
                    </>
                  )}
                </StaticPage>
              </>
            );
          }}
        </PostPageContext.Consumer>
      </PostPageProvider>
    );
  }

  // Misc. State checks
  const isPopupDisabled = !viewingAsNewVisitor && (Boolean(cookies.popup_closed) || isLoggedIn);
  const isGatedContentDisabled =
    !viewingAsNewVisitor &&
    (Boolean(cookies.email_capture_disabled) ||
      isLoggedIn ||
      (isDraft && (currentTierId !== '' || currentSubscriberId !== '')));
  const isPostPremium = post.audience === 'premium';
  const isAdmin = Boolean(admin);
  const isSubscriptionPremium = subscription?.tier === 'premium';
  const hidePremiumComments = isPostPremium && !isSubscriptionPremium && !isAdmin && !isDraft;
  const showTruncated = post.contains_subscriber_break && !isGatedContentDisabled;

  // Recommended Reading State
  // biome-ignore lint/correctness/useHookAtTopLevel: this is only rendered if not PostPage V2
  const recommendedPosts = useRecommendedPosts(post.id, !isDraft);
  const showRecommendedPostsNode = recommendedPosts?.length > 0 && recommendedReadingEnabled && !isDraft;

  const postPadding = publication.web_template.post_padding;
  return (
    <>
      {/** MISC */}
      {!onlyRenderComments && <StocktwitsEmbed />}
      <StaticPage site={site} forceShowFooter forceShowNavbar isSearchModalEnabled>
        {!onlyRenderComments && (
          <>
            <OnScrollActions
              post={post}
              publication={publication}
              isGatedContentDisabled={isGatedContentDisabled}
              isPopupDisabled={isPopupDisabled}
            />
            <TwitterEmbed updateOn={isPopupDisabled} />
            <BlueskyEmbed updateOn={true} />
          </>
        )}
        {/** POST */}
        {!onlyRenderComments && (
          <>
            <PostHTML
              // show truncated html if subscriber gate is present and not logged in
              html={showTruncated ? truncatedHtml : html}
              isGatedContentDisabled={isGatedContentDisabled}
              subscription={subscription}
              admin={admin}
              post={post}
              publication={publication}
            />
            <div className="px-4">
              <GatedContent
                post={post}
                publication={publication}
                isGatedContentDisabled={isGatedContentDisabled}
                subscription={subscription}
                paywall={paywall}
              />
            </div>
          </>
        )}

        {/** COMMENTS + RECOMMENDED READING */}
        <div id="comments" className="px-4 space-y-8">
          {commentsEnabled && !hidePremiumComments && (
            <ComponentErrorBoundary
              fallbackRender={({ error }) => (
                <PostPaddingContainer classNames="mx-auto max-w-2xl" postPadding={postPadding}>
                  <DefaultErrorBoundaryFallback error={error} />
                </PostPaddingContainer>
              )}
            >
              <CommentsSection post={post} subscription={subscription} publication={publication} />
            </ComponentErrorBoundary>
          )}

          {showRecommendedPostsNode && (
            <RecommendedPosts key={post.id} publication={publication} recommendedPosts={recommendedPosts} />
          )}
        </div>
      </StaticPage>
    </>
  );
}

export const shouldRevalidate = shouldRevalidateLoaderData;
